import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { cx } from '../cx';
import { Icon, type IconName } from '../Icon/Icon';
import { Spinner } from '../Spinner/Spinner';

/**
 * INV-14: our own component library. Application code imports from `src/ui/`,
 * never a primitive library directly - Radix is wrapped here, and only here.
 *
 * Styling goes through the design tokens, so no colour literal appears in a
 * component.
 */

export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'push'
  | 'prominent'
  | 'text';
/** `sm` is desktop-only chrome (toolbars, table headers). Touch surfaces use `md`. */
export type ButtonSize = 'sm' | 'md';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  readonly variant?: ButtonVariant | undefined;
  readonly size?: ButtonSize | undefined;
  readonly icon?: IconName | undefined;
  readonly loading?: boolean | undefined;
  /** Fills the row - the default shape for primary actions on a phone. */
  readonly block?: boolean | undefined;
  /**
   * The underlying `<button>`, for the callers that have to FOCUS one (React 19 passes a
   * ref as an ordinary prop, so it needs no `forwardRef` - only this declaration). Added
   * for the file field's "Add files", which is where focus lands when the row that had it
   * was the one just removed.
   */
  readonly ref?: Ref<HTMLButtonElement> | undefined;
  readonly children: ReactNode;
}

// `rounded-control` is generated from the --radius-control token registered in
// @theme. Reach for a token through its generated utility, never through the
// arbitrary-value bracket form: in Tailwind 4 that emits the raw text as the
// value instead of wrapping it in var(), which is invalid CSS and fails
// silently. Note that Tailwind scans source files as plain text, so even a
// class name written inside a comment ends up in the output.
const BASE =
  // `--radius-button` rather than `--radius-control`: our actions are capsules
  // (iOS) without every input in the product turning into a pill. Two tokens,
  // because button shape and control shape are two decisions.
  'inline-flex items-center justify-center gap-2 rounded-(--radius-button) ' +
  'tracking-normal transition-colors select-none ' +
  'disabled:opacity-50';

/**
 * THE LABEL'S TYPE, PER VARIANT (fe-foundation, 2026-10-04). It was one `text-sm font-medium`
 * in BASE for every variant; the text action needs the 13px meta size and the regular weight,
 * and two font-size utilities on one element are settled by the stylesheet's order rather
 * than by the class string. So the type moved out of BASE into this map, and every variant
 * that existed before reads exactly the classes it read before.
 */
const TYPE: Record<ButtonVariant, string> = {
  primary: 'text-sm font-medium',
  secondary: 'text-sm font-medium',
  ghost: 'text-sm font-medium',
  danger: 'text-sm font-medium',
  push: 'text-sm font-medium',
  prominent: 'text-sm font-semibold',
  text: 'text-2xs font-normal',
};

const SIZES: Record<ButtonSize, string> = {
  /**
   * THE FLOOR, READ FROM THE TOKEN (E5). `--touch-target-min` is 44px on a phone and
   * 40px at and above the desktop breakpoint, so the same class gives a finger Apple's
   * published hit region and a pointer the dense row this product is built on. It was
   * the literal `min-h-10` until 2026-09-07, which is how the phone ended up measured
   * against the pointer's floor.
   */
  md: 'min-h-(--touch-target-min) px-3.5 py-2',
  /**
   * `sm` DRAWS at 32px and is PRESSED at 40 (`ui-foundation.md` R5, wave 2). Desktop
   * chrome wants the smaller capsule and the measure rule wants the floor; `.hit-expand`
   * is how the product has answered that twice already (Checkbox, Switch) - a centred
   * pseudo-element grows what the pointer can hit and changes no pixel that is painted.
   */
  sm: 'hit-expand min-h-8 px-2.5 py-1',
};

const VARIANTS: Record<ButtonVariant, string> = {
  // iOS 26 toolbar language: every capsule is neutral GLASS with a MONOCHROME
  // label (Arif, 2026-07-28: no blue on buttons, not even as a text tint).
  // The primary action is distinguished by weight alone.
  primary: 'glass-control font-semibold text-ink',
  secondary: 'glass-control text-ink',
  // The three above press by GIVING (the scale in `.glass-control:active`). A
  // ghost button has no material to squish, so it presses by taking the surface
  // one step past hover - `.pressable`, instant in, eased out (index.css).
  ghost: 'pressable text-ink-muted hover:bg-surface-hover hover:text-ink',
  // iOS destructive: a RED LABEL on a normal control, not a red outline - the
  // outline read as "a badly rendered line" (Arif, 2026-07-28).
  danger: 'glass-control text-danger',
  /* --- p4-list wave 3 --- START ------------------------------------------------
     THE KIT'S CONTENT-AREA PUSH BUTTON (L-B2; owner 2026-09-10 on the list's capsule
     row, "diese buttons wirken alt", and again on the pane's Open and the footer's
     Show 50 more).

     WHY THE OTHER FOUR VARIANTS ARE WRONG ON THE CONTENT GROUND: all four are
     `.glass-control`, and Liquid Glass is not a content material. HIG Materials, verbatim:
     "Don't use Liquid Glass in the content layer. Liquid Glass works best when it provides
     a clear distinction between interactive elements and content, and including it in the
     content layer can result in unnecessary complexity and a confusing visual hierarchy.
     Instead, use standard materials for elements in the content layer"
     (hig: https://developer.apple.com/design/human-interface-guidelines/materials).

     WHAT THE KIT SHIPS INSTEAD is a translucent ink wash and NO ring: the layer style
     `Content Area/Light/01 - Bordered/01 - Idle` is a single fill, #000000 at alpha 0.08,
     with no border and no stroke anywhere in the master (kit:
     `Buttons/Light|Dark/Content Area/Bordered/{1 Mn..5 XL}/Active, Off, 1 Idle`, and the
     Bordered family carries only Idle, Clicked and Disabled - there is no hover state in
     either appearance, which is why this variant does not paint one). The hairline capsule
     it replaces was the toolbar's own construction borrowed onto the page, and the ring is
     what the owner read as old. Both fills are tokens with the master named
     (`--color-content-button`, `--color-content-button-pressed`).

     `.push-control` carries the paint (index.css, p4-list wave-3 block) for the reason
     `.glass-control` does: a cascade layer beats specificity, and a 13px label - the kit's
     size for this family at every height - has to win against `text-sm` in BASE. --- */
  push: 'push-control',
  /* --- p4-list wave 3 --- END -------------------------------------------------- */
  /* --- fe-foundation (redesign, 2026-10-04) --- START -----------------------------
     THE ONE PRIMARY BUTTON OF A REDESIGNED PAGE, IN THE CONTENT AREA (register L1, A4; HIG
     Buttons "prominent style ... the accent colour as its background", hig:
     https://developer.apple.com/design/human-interface-guidelines/buttons). Apple's accent
     value, not darkened, with the white label in both appearances (`--color-on-accent`, the
     kit's Glass Prominent title). No hover: the kit's content button ships Idle, Clicked and
     Disabled and nothing else (components page section 2). The disabled look is the
     product's existing one (`disabled:opacity-50` in BASE; open question Q-FND-3). In the
     TOOLBAR the page's primary keeps the existing route - `variant="primary"` is promoted to
     the accent-tinted capsule by `WindowToolbar` (glass.css, `.shell-toolbar-prominent`) -
     because none of the five pages puts its primary there. */
  prominent: 'bg-accent text-(--color-on-accent)',
  /* A TEXT ACTION (register R-K1 "Mark as handled" as blue text action, rule R32): the accent
     ink and no fill, visible at rest (L11), the global keyboard ring, and the press region
     grown by `.hit-expand` without a painted pixel. Used by Mark as handled, Pause, Retry
     now, More and Less. The size prop does not apply: a text action is as tall as its line. */
  text: 'hit-expand px-1 py-0.5 whitespace-nowrap text-accent hover:underline',
  /* --- fe-foundation --- END ------------------------------------------------------ */
};

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  loading = false,
  block = false,
  children,
  className,
  type = 'button',
  disabled,
  ...rest
}: ButtonProps) {
  return (
    <button
      type={type}
      disabled={disabled === true || loading}
      aria-busy={loading || undefined}
      /**
       * WHICH BUTTON MASTER THIS IS, SAID BY THE COMPONENT THAT KNOWS (wave 3 round 2,
       * finding 1). A `Button` always carries a label - `children` is required - so it is
       * the kit's TEXT button and is as wide as its label plus the master's padding; an
       * `IconButton` is the symbol master and is a square. Chrome that has to seat the two
       * differently (the window toolbar) reads the mark instead of guessing from the DOM,
       * which is what went wrong: `:has(> svg:only-child)` counts ELEMENT children, so a
       * button holding an icon and a text node looked symbol-only and was given the 28px
       * square, with its label drawn across its neighbours.
       */
      data-control="text"
      className={cx(
        BASE,
        TYPE[variant],
        variant === 'text' ? undefined : SIZES[size],
        VARIANTS[variant],
        block && 'w-full',
        className,
      )}
      {...rest}
    >
      {loading ? <Spinner /> : icon !== undefined ? <Icon name={icon} /> : null}
      {children}
    </button>
  );
}
